import { getEnv, getRoot, IStateTreeNode } from "mobx-state-tree";
import { ArtichokeService } from "@fidget/common/services/artichokeService";
import { UserActivityService } from "@fidget/common/services/userActivityService";
import { WsGlobalEventsListener } from "@fidget/common/services/eventsListener";
import { RingtoneService } from "../services/ringtoneService";
import { IRootStore, RootStoreEnv } from ".";
import { IAgentStore } from "./agent/AgentStore";
import { IAgentGroupStore } from "./agentGroups/agentGroupsStore";
import { ICalendarStore } from "./calendar/CalendarStore";
import { ICallStore } from "./call/CallStore";
import { ICurrentUserStore } from "./currentUser/CurrentUserStore";
import { IUserProfile } from "./currentUser/UserProfile";
import { IInboxStore } from "./inbox/InboxStore";
import { IIntentStore } from "./intent/IntentStore";
import { IMessageWidgetsStore } from "./messageWidgets/MessageWidgetsStore";
import { IReportsStore } from "./reports/reportsStore";
import { IRoleStore } from "./role/RoleStore";
import { IRoomStore } from "./room/RoomStore";
import { IRoom } from "./room/Room";
import { IRouterStore } from "./router/RouterStore";
import { ITagGroupsStore } from "./tagGroups/TagGroupStore";
import { ITopicStore } from "./topics/topicStore";
import { IUiStore } from "./ui/UiStore";
import { IWidgetStore } from "./widget/WidgetStore";
import { ITagsStore } from "./tags/TagsStore";
import { SpinnerService } from "../services/spinner/spinnerService";
import { IGuestStore } from "./guest/GuestStore";
import { IUsersStore } from "./users/UsersStore";
import { AgentCallService } from "../services/AgentCallService";

export const servicesGetters = (self: IStateTreeNode) => {
  const getters = {
    get spinnerService(): SpinnerService {
      return (getEnv(self) as RootStoreEnv).spinnerService;
    },
    get artichokeService(): ArtichokeService {
      return (getEnv(self) as RootStoreEnv).artichokeService;
    },
    get userActivityService(): UserActivityService {
      return (getEnv(self) as RootStoreEnv).userActivityService;
    },
    get ringtoneService(): RingtoneService {
      return (getEnv(self) as RootStoreEnv).ringtoneService;
    },
    get eventListener(): WsGlobalEventsListener {
      return (getEnv(self) as RootStoreEnv).eventListener;
    },
    get callService(): AgentCallService {
      return (getEnv(self) as RootStoreEnv).callService;
    }
  };
  return getters;
};

export const agentStoreGetter = (self: IStateTreeNode) => {
  const getter = {
    get agentStore(): IAgentStore {
      return (getRoot(self) as IRootStore).agentStore;
    }
  };
  return getter;
};

export const agentGroupsStoreGetter = (self: IStateTreeNode) => {
  return {
    get agentGroupsStore(): IAgentGroupStore {
      return (getRoot(self) as IRootStore).agentGroupStore;
    }
  };
};

export const inboxStoreGetter = (self: IStateTreeNode) => {
  const getter = {
    get inboxStore(): IInboxStore {
      return (getRoot(self) as IRootStore).inboxStore;
    }
  };
  return getter;
};

export const calendarStoreGetter = (self: IStateTreeNode) => {
  const getter = {
    get calendarStore(): ICalendarStore {
      return (getRoot(self) as IRootStore).calendarStore;
    }
  };
  return getter;
};

export const currentUserStoreGetter = (self: IStateTreeNode) => {
  const getter = {
    get currentUserStore(): ICurrentUserStore {
      return (getRoot(self) as IRootStore).currentUserStore;
    }
  };
  return getter;
};

export const guestStoreGetter = (self: IStateTreeNode) => {
  const getter = {
    get guestStore(): IGuestStore {
      return (getRoot(self) as IRootStore).guestStore;
    }
  };
  return getter;
};

export const usersStoreGetter = (self: IStateTreeNode) => {
  const getter = {
    get usersStore(): IUsersStore {
      return (getRoot(self) as IRootStore).usersStore;
    }
  };
  return getter;
};

export const callStoreGetter = (self: IStateTreeNode) => {
  const getter = {
    get callStore(): ICallStore {
      return (getRoot(self) as IRootStore).callStore;
    }
  };
  return getter;
};

export const currentUserProfileGetter = (self: IStateTreeNode) => {
  const getter = {
    get currentUserProfile(): IUserProfile | undefined {
      return (getRoot(self) as IRootStore).currentUserStore.profile;
    }
  };
  return getter;
};

export const roomStoreGetter = (self: IStateTreeNode) => {
  const getter = {
    get roomStore(): IRoomStore {
      return (getRoot(self) as IRootStore).roomStore;
    }
  };
  return getter;
};

export const routerStoreGetter = (self: IStateTreeNode) => {
  const getter = {
    get routerStore(): IRouterStore {
      return (getRoot(self) as IRootStore).routerStore;
    }
  };
  return getter;
};

export const intentStoreGetter = (self: IStateTreeNode) => {
  const getter = {
    get intentStore(): IIntentStore {
      return (getRoot(self) as IRootStore).intentStore;
    }
  };
  return getter;
};

export const widgetStoreGetter = (self: IStateTreeNode) => {
  const getter = {
    get widgetStore(): IWidgetStore {
      return (getRoot(self) as IRootStore).widgetStore;
    }
  };
  return getter;
};

export const reportsStoreGetter = (self: IStateTreeNode) => {
  const getter = {
    get reportsStore(): IReportsStore {
      return (getRoot(self) as IRootStore).reportsStore;
    }
  };
  return getter;
};

export const uiStoreGetter = (self: IStateTreeNode) => {
  const getter = {
    get uiStore(): IUiStore {
      return (getRoot(self) as IRootStore).uiStore;
    }
  };
  return getter;
};

export const currentRoomGetter = (self: IStateTreeNode) => {
  const getter = {
    get currentRoomReference(): IRoom | undefined {
      return (getRoot(self) as IRootStore).roomStore.currentRoom as IRoom;
    }
  };
  return getter;
};

export const roleStoreGetter = (self: IStateTreeNode) => {
  const getter = {
    get roleStore(): IRoleStore {
      return (getRoot(self) as IRootStore).roleStore;
    }
  };
  return getter;
};

export const messageWidgetsStoreGetter = (self: IStateTreeNode) => {
  const getter = {
    get messageWidgetsStore(): IMessageWidgetsStore {
      return (getRoot(self) as IRootStore).messageWidgetsStore;
    }
  };

  return getter;
};

export const tagsStoreGetter = (self: IStateTreeNode) => {
  const getter = {
    get tagsStore(): ITagsStore {
      return (getRoot(self) as IRootStore).tagsStore;
    }
  };

  return getter;
};

export const tagGroupsStoreGetter = (self: IStateTreeNode) => {
  const getter = {
    get tagGroupsStore(): ITagGroupsStore {
      return (getRoot(self) as IRootStore).tagGroupsStore;
    }
  };

  return getter;
};

export const topicStoreGetter = (self: IStateTreeNode) => {
  const getter = {
    get topicStore(): ITopicStore {
      return (getRoot(self) as IRootStore).topicStore;
    }
  };

  return getter;
};

export const minutesFromMillis = (millis: number): number => {
  return Math.floor(millis / (1000 * 60));
};

export const secondsWithoutMinutesFromMillis = (millis: number, minutes: number): number => {
  return Math.floor((millis - minutes * 60 * 1000) / 1000);
};

export const minutesAndSecondsToMillis = (minutes: number, seconds: number): number => {
  return minutes * 60 * 1000 + seconds * 1000;
};
